import { z } from 'zod';

import { mapConcurrently } from '@shared/lib/async/map-concurrently';

import { apiClient } from './client';
import { createCampaignQuestionLoader } from './campaign-question';
import { parseCampaignAnswer, parseCampaignStart, parseCampaignHint } from './campaign-answer-contract';
import { readPublishedCampaigns, campaignProgressPackSchema, rewardsPairSchema } from './campaign-catalogue-contract';

const packSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  kind: z.enum(['film', 'series', 'game', 'anime']),
  difficulty: z.number().int().min(1).max(5),
  questionIds: z.array(z.string().min(1)).min(1),
});
export type CampaignPack = z.infer<typeof packSchema>;
const titlesSchema = z.object({
  data: z.array(z.object({ id: z.string(), name: z.string() })),
  total: z.number().int().nonnegative(),
});
export type CampaignPlayContext = { campaignId: string; packId: string };

const loadQuestion = createCampaignQuestionLoader(
  (id) => apiClient.questions.getById(id),
  async (ids) => titlesSchema.parse(await apiClient.titles.getByIds({ ids })),
);

export const campaignApi = {
  async catalogue(authenticated = false) {
    const campaigns = await readPublishedCampaigns((query) => apiClient.campaign.list(query));
    const refs = campaigns.flatMap((campaign) => campaign.packIds.map((id) => ({ id, campaignId: campaign.id })));
    const chapters = await mapConcurrently(refs, 6, async ({ id, campaignId }) => {
      const pack = packSchema.parse(await apiClient.packs.getById(id));
      if (pack.id !== id) throw new Error('Chapter ID mismatch');
      return { ...pack, campaignId };
    });
    const states = authenticated
      ? await mapConcurrently(campaigns, 6, async (campaign) => await campaignApi.progress(campaign.id))
      : [];
    return { campaigns, chapters, states };
  },
  async progress(id: string) {
    const state = z
      .object({
        campaignId: z.string(),
        packs: z.array(campaignProgressPackSchema),
        earnedRewards: rewardsPairSchema,
        totalRewards: rewardsPairSchema,
      })
      .parse(await apiClient.campaign.getPlay(id));
    if (state.campaignId !== id) throw new Error('Campaign state mismatch');
    return state;
  },
  question: loadQuestion,
  async start(context: CampaignPlayContext) {
    const started = parseCampaignStart(await apiClient.campaign.startPlay(context));
    if (started.campaignId !== context.campaignId || started.pack.packId !== context.packId) {
      throw new Error('Campaign start context mismatch');
    }
    return started;
  },
  async answer(context: CampaignPlayContext, questionId: string, titleId?: string, actionId?: string) {
    const answer = parseCampaignAnswer(
      await apiClient.campaign.answerPlay(
        { ...context, questionId, titleId, timeout: titleId === undefined, actionId },
        actionId ? { idempotencyKey: actionId } : undefined,
      ),
    );
    if (answer.pack.packId !== context.packId) throw new Error('Campaign answer pack mismatch');
    return answer;
  },
  async hint(
    context: CampaignPlayContext,
    questionId: string,
    hint: 'fifty-fifty' | 'freeze' | 'swap-frame',
    actionId: string,
  ) {
    return parseCampaignHint(
      await apiClient.campaign.usePlayHint({ ...context, questionId, hint, actionId }, { idempotencyKey: actionId }),
    );
  },
};
